import "./RichText.scss";

import classNames from "classnames";
import { useTranslations } from "providers";
import Quill from "quill";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { logger } from ".";
import { annotateToolbarTooltips } from "./extensions/annotateToolbarTooltips";
import { blockImageDrop } from "./extensions/blockImageDrop";
import { markManualNumbering } from "./extensions/markManualNumbering";
import { getHtml } from "./getHtml";
import { RichTextLinkDialog } from "./LinkDialog/RichTextLinkDialog";
import { useRichTextLinkDialog } from "./LinkDialog/useRichTextLinkDialog";
import { PasteOptionsRequest, PasteOptionsWidget } from "./PasteDialog/PasteOptionsWidget";
import { setupPasteDialog } from "./PasteDialog/setupQuillClipboard";
import { getRichTextQuillOptions } from "./richTextQuillOptions";
import { RichTextSourceViewer } from "./ViewSource/RichTextSourceViewer";

export interface RichTextProps {
  value: string;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  name: string;
}

export const RichText: React.FC<RichTextProps> = ({
  name,
  autoFocus,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  className,
  required,
}) => {
  const i18n = useTranslations();

  const editorRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  const [quill, setQuill] = useState<Quill | null>(null);
  const [showSource, setShowSource] = useState(false);
  const [showPasteOptions, setShowPasteOptions] = useState<PasteOptionsRequest | null>(null);
  const { linkDialogProps, setupLinkHandlers } = useRichTextLinkDialog(quillRef, onChangeRef);
  const placeholderToUse = placeholder || i18n.Richtext_Placeholder;

  const handleKeepFormatting = useCallback(() => {
    setShowPasteOptions(null);
  }, []);

  const handlePlainText = useCallback(() => {
    setShowPasteOptions(null);
    showPasteOptions?.onPlainText?.();
  }, [showPasteOptions]);

  const handleCancel = useCallback(() => {
    setShowPasteOptions(null);
    showPasteOptions?.onCancel();
  }, [showPasteOptions]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let qr = quillRef.current;
    const er = editorRef.current;
    if (er && !qr) {
      qr = new Quill(er, getRichTextQuillOptions(placeholderToUse));
      quillRef.current = qr;
      setQuill(qr);

      setupPasteDialog(qr, setShowPasteOptions);
      markManualNumbering(qr);

      qr.on("selection-change", (range) => {
        if (!wrapperRef.current) return;
        if (range !== null) {
          wrapperRef.current.classList.add("ql-focus");
        } else {
          wrapperRef.current.classList.remove("ql-focus");
        }
      });

      setupLinkHandlers(qr);

      // custom clipboard handles paste choices; no DOM paste listener required
      blockImageDrop(er, name);

      const toolbar = qr.container.previousElementSibling as HTMLElement | null;
      if (toolbar) {
        annotateToolbarTooltips(toolbar, [
          { selector: "button.ql-bold", title: i18n.RichText_Tooltip_Bold },
          { selector: "button.ql-italic", title: i18n.RichText_Tooltip_Italic },
          { selector: "button.ql-underline", title: i18n.RichText_Tooltip_Underline },
          { selector: "button.ql-strike", title: i18n.RichText_Tooltip_Strike },
          { selector: 'buttoql-script[value="sub"]', title: i18n.RichText_Tooltip_Script_Sub },
          { selector: 'button.ql-script[value="super"]', title: i18n.RichText_Tooltip_Script_Super },
          { selector: ".ql-header .ql-pin.cker-label", title: i18n.RichText_Tooltip_Heading },
          { selector: ".ql-color .ql-picker-label", title: i18n.RichText_Tooltip_Color },
          { selector: ".ql-background .ql-picker-label", title: i18n.RichText_Tooltip_Background },
          { selector: 'button.ql-list[value="ordered"]', title: i18n.RichText_Tooltip_List_Ordered },
          { selector: 'button.ql-list[value="bullet"]', title: i18n.RichText_Tooltip_List_Bullet },
          { selector: 'button.ql-indent[value="-1"]', title: i18n.RichText_Tooltip_Indent_Minus1 },
          { selector: 'button.ql-indent[value="+1"]', title: i18n.RichText_Tooltip_Indent_Plus1 },
          { selector: "button.ql-blockquote", title: i18n.RichText_Tooltip_Blockquote },
          { selector: "button.ql-link", title: i18n.RichText_Tooltip_Link },
          { selector: "button.ql-clean", title: i18n.RichText_Tooltip_Clean },
          { selector: "button.ql-source", title: i18n.RichText_Tooltip_Source },
        ]);
      }
    }

    if (!qr) return;
    qr.on("text-change", () => {
      const newValue = getHtml(qr);
      logger.trace(`${name}: onChange():`, newValue);
      onChange(newValue);
    });
  }, [onChange, placeholderToUse, required, name, setupLinkHandlers, i18n]);

  useEffect(() => {
    const qr = quillRef.current;
    if (!qr) return;
    if (autoFocus) {
      logger.trace(`${name}: set autofocus`, qr);
      qr.focus();
    }
  }, [quillRef, autoFocus, name]);

  useEffect(() => {
    const qr = quillRef.current;
    if (!qr) return;
    const after = getHtml(qr);

    if (showSource) {
      return;
    }

    if (value !== after) {
      logger.trace(`${name}: prop value changed:`, value);
      const delta = qr.clipboard.convert({ html: value });
      qr.setContents(delta);
    }
  }, [name, showSource, value]);

  className = classNames("ql-container", "ql-snow", className);

  const { key, ...otherProps } = linkDialogProps;

  return (
    <div ref={wrapperRef} className={classNames("sbb-quill", "ql-toolbar-dim", { "ql-source-mode": showSource })}>
      <div
        ref={editorRef}
        className={className}
        onFocus={() => {
          logger.trace(`${name}: onFocus():`);
          return onFocus?.();
        }}
        onBlur={() => {
          logger.trace(`${name}: onBlur():`);
          onBlur?.();
        }}
      />
      <RichTextSourceViewer quill={quill} value={value} onChange={onChange} onModeChange={setShowSource} />
      <RichTextLinkDialog key={key} {...otherProps} />
      {showPasteOptions && (
        <PasteOptionsWidget
          anchorRect={showPasteOptions.anchorRect}
          onKeepFormatting={handleKeepFormatting}
          onPlainText={showPasteOptions.onPlainText ? handlePlainText : undefined}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};
